import type { User } from '@features/auth/auth-client-adapter'
import { Task, Update, defModule, type TaskMessage, type UpdateResult } from '@st/redux'
import type { AuthError, LookupFolderResult, OAuthProvider, PublicFolder } from '@st/sdk'
import { removeQueryParams } from '@st/util/url'
import { Effect, pipe } from 'effect'
import { isMatching, match, P } from '@st/pattern'
import { Auth, Routing } from '~/effects'
import { observeAuth, observeOrganizationRoute } from '~/features/module-runtime'
import { toPath } from '~/features/routes'
import {
  loadPublicFolder,
  lookupFolders,
  redirectToOAuthLogin,
  sendLookupVerificationEmail,
  sendOrganizerVerificationEmail
} from './client-login-flow-tasks'

type AuthState =
  | { status: 'unknown' }
  | { status: 'loggedIn'; user: User }
  | { status: 'loggedOut'; error: AuthError | undefined }

type AuthChanged = {
  type: 'authChanged'
  auth: AuthState
}

const observeClientLoginAuth = observeAuth(
  (event): AuthChanged =>
    match(event)
      .returnType<AuthChanged>()
      .with({ type: 'loggedIn' }, ({ user }) => ({
        type: 'authChanged',
        auth: { status: 'loggedIn', user }
      }))
      .with({ type: 'loggedOut' }, () => ({
        type: 'authChanged',
        auth: { status: 'loggedOut', error: undefined }
      }))
      .with({ type: 'loginFailed' }, ({ error }) => ({
        type: 'authChanged',
        auth: { status: 'loggedOut', error }
      }))
      .exhaustive()
)

const observeClientLoginRoute = observeOrganizationRoute({
  name: P.union(
    'client_folder',
    'client_folder_logged_out',
    'client_questionnaire',
    'client_questionnaire_legacy'
  )
})

type ClientLoginOrganizationContext = TaskMessage<typeof observeClientLoginRoute>['context']
type ClientLoginRoute = Exclude<ClientLoginOrganizationContext, { status: 'inactive' }>['route']
type OrganizerRoute = Extract<
  ClientLoginRoute,
  { name: 'client_folder' | 'client_folder_logged_out' }
>
type LookupRoute = Extract<
  ClientLoginRoute,
  { name: 'client_questionnaire' | 'client_questionnaire_legacy' }
>

type OrganizerFlow = {
  kind: 'organizer'
  route: OrganizerRoute
  publicFolder: PublicFolder | undefined
}

type OrganizerLoginFlow = OrganizerFlow & {
  publicFolder: PublicFolder
}

type LookupFlow = {
  kind: 'lookup'
  route: LookupRoute
  organizationId: string
  clientKind: 'new' | 'returning'
}

type ActiveFlow = OrganizerFlow | LookupFlow
type LoginFlow = OrganizerLoginFlow | LookupFlow

type IdleState = {
  status: 'idle'
  auth: AuthState
}

type LoadingState =
  | {
      status: 'loading'
      route: OrganizerRoute
      auth: AuthState
      publicFolder: PublicFolder | undefined
    }
  | {
      status: 'loading'
      route: LookupRoute
      auth: AuthState
      organizationId: string | undefined
    }

type NotFoundState = {
  status: 'notFound'
  route: ClientLoginRoute
  auth: AuthState
}

type LoginState =
  | {
      status: 'login'
      flow: LoginFlow
      loginMethod: 'emailVerification'
      authError: AuthError | undefined
      selectedUserId: string | undefined
    }
  | {
      status: 'login'
      flow: OrganizerLoginFlow
      loginMethod: 'oauth'
      provider: OAuthProvider
      authError: AuthError | undefined
      selectedUserId: string | undefined
    }

type SendingVerificationEmailState = {
  status: 'sendingVerificationEmail'
  flow: LoginFlow
  selectedUserId: string
}

type ClickEmailVerificationLinkState = {
  status: 'clickEmailVerificationLink'
  flow: LoginFlow
  selectedUserId: string
}

type LookupOrganizerNotFoundState = {
  status: 'lookupOrganizerNotFound'
  flow: LookupFlow
  email: string
}

type LoggedInState = {
  status: 'loggedIn'
  flow: ActiveFlow
  currentUser: User
}

type LookupPendingState = {
  status: 'lookupPending'
  flow: LookupFlow
  currentUser: User
}

type NavigatingToFolderState = {
  status: 'navigatingToFolder'
  flow: LookupFlow
  currentUser: User
  folderId: string
}

type LookupChooseFolderState = {
  status: 'lookupChooseFolder'
  flow: LookupFlow
  currentUser: User
  folders: LookupFolderResult['folders']
}

type LookupNoneFoundState = {
  status: 'lookupNoneFound'
  flow: LookupFlow
  currentUser: User
}

type LookupFailedState = {
  status: 'lookupFailed'
  flow: LookupFlow
  currentUser: User
}

type ActiveClientLoginFlowState =
  | IdleState
  | LoadingState
  | NotFoundState
  | LoginState
  | SendingVerificationEmailState
  | ClickEmailVerificationLinkState
  | LookupOrganizerNotFoundState
  | LoggedInState
  | LookupPendingState
  | NavigatingToFolderState
  | LookupChooseFolderState
  | LookupNoneFoundState
  | LookupFailedState

type AcceptedOperationState = SendingVerificationEmailState | LookupPendingState

type FinishingState = {
  status: 'finishing'
  operation: 'verificationEmail' | 'folderLookup'
  resume: ActiveClientLoginFlowState
}

export type ClientLoginFlowState = ActiveClientLoginFlowState | FinishingState

export type ClientLoginFlowMessage =
  | TaskMessage<typeof observeClientLoginRoute>
  | TaskMessage<typeof observeClientLoginAuth>
  | TaskMessage<typeof loadPublicFolder>
  | { type: 'verificationEmailSent' }
  | { type: 'verificationEmailFailed'; error: AuthError }
  | { type: 'lookupOrganizerNotFound'; email: string }
  | { type: 'folderCreated'; folderId: string }
  | { type: 'foldersFound'; folders: LookupFolderResult['folders'] }
  | { type: 'folderLookupFailed' }
  | { type: 'lookupFolderChosen'; folderId: string }
  | { type: 'retryFolderLookup' }
  | { type: 'clickSendVerificationEmail'; recipientUserId: string }
  | { type: 'tryAnotherEmail' }
  | { type: 'logoutRequested' }

/**
 * Client-login topology:
 *
 * idle --matching route--> loading
 *                           | organizer + logged out: public folder
 *                           | lookup: organization
 *                           | auth + route data resolved
 *                           v
 *             loggedIn <-- login --send link--> sendingVerificationEmail
 *                 |                               | sent
 *                 |                               v
 *                 |                    clickEmailVerificationLink
 *                 |                               | authenticated
 *                 +-------------------------------+
 *                 |
 *                 +-- lookup --> lookupPending --> navigatingToFolder
 *                                      |          choose / none / failed --retry--+
 *
 * Route and auth are authoritative observations. Resource resolution and the
 * OAuth login redirect are state-owned Tasks. Verification email and folder
 * lookup are Commands: both endpoints accept durable writes, so replacing the
 * visible context moves their ownership to `finishing` instead of canceling the
 * work. `finishing` defers any new context's lookup until the accepted write
 * settles. Logout and folder navigation are Commands for the same ownership reason.
 *
 * The reducer receives domain outcomes, never SDK envelopes or request IDs.
 * Absolute URLs combine the route in state with the browser origin captured at
 * the app root. A Task never samples ambient location after its transition.
 */
export const clientLoginFlowModule = defModule<{ state: ClientLoginFlowState; message: ClientLoginFlowMessage }>({
  init: () => ({ status: 'idle', auth: { status: 'unknown' } }),
  tasks: clientLoginFlowTasks,
  update: pipe(
    updateClientLoginFlow,
    Update.ignoreWhen<ClientLoginFlowState, ClientLoginFlowMessage>(isIgnoredClientLoginIntent)
  )
})

function isIgnoredClientLoginIntent({
  state,
  message
}: Readonly<{ state: ClientLoginFlowState; message: ClientLoginFlowMessage }>) {
  return match(message)
    .with(
      { type: 'clickSendVerificationEmail' },
      () =>
        state.status != 'clickEmailVerificationLink' &&
        !(state.status == 'login' && state.loginMethod == 'emailVerification')
    )
    .with(
      { type: 'lookupFolderChosen' },
      () => !isMatching({ status: 'lookupChooseFolder' }, state)
    )
    .with(
      { type: 'tryAnotherEmail' },
      () => !isMatching({ status: 'lookupOrganizerNotFound' }, state)
    )
    .with({ type: 'retryFolderLookup' }, () => !isMatching({ status: 'lookupFailed' }, state))
    .with(
      { type: 'logoutRequested' },
      () =>
        state.status != 'lookupNoneFound' &&
        !isMatching({ status: 'loggedIn', flow: { kind: 'organizer' } }, state)
    )
    .otherwise(() => false)
}

function clientLoginFlowTasks(state: ClientLoginFlowState) {
  const observations = [observeClientLoginRoute(), observeClientLoginAuth()]

  return match(state)
    .with(
      {
        status: 'loading',
        route: { name: P.union('client_folder', 'client_folder_logged_out') },
        auth: { status: 'loggedOut' },
        publicFolder: undefined
      },
      (state) => [...observations, loadPublicFolder(state.route.folderId)]
    )
    .with({ status: 'login', loginMethod: 'oauth' }, (state) => [
      ...observations,
      redirectToOAuthLogin(state.provider, removeQueryParams(toPath(state.flow.route), ['aud']))
    ])
    .otherwise(() => observations)
}

function updateClientLoginFlow(
  state: ClientLoginFlowState,
  message: ClientLoginFlowMessage
): UpdateResult<ClientLoginFlowState, ClientLoginFlowMessage> {
  return match({ state, message })
    .returnType<UpdateResult<ClientLoginFlowState, ClientLoginFlowMessage>>()
    .with({ message: { type: 'organizationRouteChanged' } }, ({ state, message }) =>
      organizationRouteChanged(state, message.context)
    )
    .with({ message: { type: 'authChanged' } }, ({ state, message }) =>
      authChanged(state, message.auth)
    )
    .with(
      {
        state: {
          status: 'loading',
          route: { name: P.union('client_folder', 'client_folder_logged_out') }
        },
        message: { type: 'publicFolderLoaded' }
      },
      ({ state, message }) => advanceLoading({ ...state, publicFolder: message.publicFolder })
    )
    .with(
      {
        state: {
          status: 'loading',
          route: { name: P.union('client_folder', 'client_folder_logged_out') }
        },
        message: { type: 'publicFolderNotFound' }
      },
      ({ state }) => ({ status: 'notFound', route: state.route, auth: state.auth })
    )
    .with(
      {
        state: P.union(
          { status: 'login', loginMethod: 'emailVerification' },
          { status: 'clickEmailVerificationLink' }
        ),
        message: { type: 'clickSendVerificationEmail' }
      },
      ({ state, message }) => startVerificationEmail(state.flow, message.recipientUserId)
    )
    .with(
      {
        state: { status: 'sendingVerificationEmail' },
        message: { type: 'verificationEmailSent' }
      },
      ({ state }) => ({
        status: 'clickEmailVerificationLink',
        flow: state.flow,
        selectedUserId: state.selectedUserId
      })
    )
    .with(
      {
        state: { status: 'sendingVerificationEmail' },
        message: { type: 'verificationEmailFailed' }
      },
      ({ state, message }) => loginState(state.flow, message.error, state.selectedUserId)
    )
    .with(
      {
        state: { status: 'sendingVerificationEmail', flow: { kind: 'lookup' } },
        message: { type: 'lookupOrganizerNotFound' }
      },
      ({ state, message }) => ({
        status: 'lookupOrganizerNotFound',
        flow: state.flow,
        email: message.email
      })
    )
    .with(
      {
        state: { status: 'lookupOrganizerNotFound' },
        message: { type: 'tryAnotherEmail' }
      },
      ({ state }) => loginState(state.flow, undefined)
    )
    .with(
      { state: { status: 'lookupPending' }, message: { type: 'folderCreated' } },
      ({ state, message }) => navigateToFoundFolder(state, message.folderId)
    )
    .with(
      { state: { status: 'lookupPending' }, message: { type: 'foldersFound' } },
      ({ state, message }) => foldersFound(state, message.folders)
    )
    .with(
      { state: { status: 'lookupPending' }, message: { type: 'folderLookupFailed' } },
      ({ state }) => ({
        status: 'lookupFailed',
        flow: state.flow,
        currentUser: state.currentUser
      })
    )
    .with(
      { state: { status: 'lookupFailed' }, message: { type: 'retryFolderLookup' } },
      ({ state }) => startFolderLookup(state.flow, state.currentUser)
    )
    .with(
      {
        state: { status: 'finishing', operation: 'verificationEmail' },
        message: {
          type: P.union(
            'verificationEmailSent',
            'verificationEmailFailed',
            'lookupOrganizerNotFound'
          )
        }
      },
      ({ state }) => resumeDeferredOperation(state.resume)
    )
    .with(
      {
        state: { status: 'finishing', operation: 'folderLookup' },
        message: { type: P.union('folderCreated', 'foldersFound', 'folderLookupFailed') }
      },
      ({ state }) => resumeDeferredOperation(state.resume)
    )
    .with(
      {
        state: { status: 'lookupChooseFolder' },
        message: { type: 'lookupFolderChosen' }
      },
      ({ state, message }) => navigateToFoundFolder(state, message.folderId)
    )
    .with(
      {
        state: P.union(
          { status: 'loggedIn', flow: { kind: 'organizer' } },
          { status: 'lookupNoneFound' }
        ),
        message: { type: 'logoutRequested' }
      },
      ({ state }) => [state, logout()]
    )
    .otherwise(({ state, message }) => unexpectedMessage(state, message))
}

function organizationRouteChanged(
  state: ClientLoginFlowState,
  context: ClientLoginOrganizationContext
): UpdateResult<ClientLoginFlowState, ClientLoginFlowMessage> {
  if (state.status == 'finishing') {
    return {
      ...state,
      resume: deferredState(organizationRouteChangedActive(state.resume, context))
    }
  }

  return preserveAcceptedOperation(state, organizationRouteChangedActive(state, context))
}

function organizationRouteChangedActive(
  state: ActiveClientLoginFlowState,
  context: ClientLoginOrganizationContext
): UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage> {
  const route = match(context)
    .returnType<ClientLoginRoute | null>()
    .with({ status: 'inactive' }, () => null)
    .otherwise((context) => context.route)
  return Update.pipe(routeChanged(state, route), (routed) => {
    if (
      routed.status != 'loading' ||
      !isMatching(
        { name: P.union('client_questionnaire', 'client_questionnaire_legacy') },
        routed.route
      )
    ) {
      return routed
    }
    if (context.status == 'unavailable') {
      return { status: 'notFound', route: routed.route, auth: routed.auth }
    }
    if (context.status == 'loaded') {
      return advanceLoading({ ...routed, organizationId: context.organization.id })
    }
    return routed
  })
}

function routeChanged(
  state: ActiveClientLoginFlowState,
  route: ClientLoginRoute | null
): UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage> {
  const auth = authFromState(state)
  if (!route) {
    return { status: 'idle', auth }
  }

  const currentRoute = routeFromState(state)
  if (currentRoute && toPath(currentRoute) == toPath(route)) {
    return state
  }

  return advanceLoading(loadingState(route, auth))
}

function authChanged(
  state: ClientLoginFlowState,
  auth: AuthState
): UpdateResult<ClientLoginFlowState, ClientLoginFlowMessage> {
  if (state.status == 'finishing') {
    return { ...state, resume: deferredState(authChangedActive(state.resume, auth)) }
  }

  return preserveAcceptedOperation(state, authChangedActive(state, auth))
}

function authChangedActive(
  state: ActiveClientLoginFlowState,
  auth: AuthState
): UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage> {
  return match(state)
    .returnType<UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage>>()
    .with({ status: 'idle' }, () => ({ status: 'idle', auth }))
    .with({ status: 'loading' }, (state) => advanceLoading({ ...state, auth }))
    .with({ status: 'notFound' }, (state) => ({ ...state, auth }))
    .with({ status: 'login' }, (state) =>
      match(auth)
        .returnType<UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage>>()
        .with({ status: 'loggedIn' }, ({ user }) => postLoginState(state.flow, user))
        .with({ status: 'loggedOut' }, ({ error }) =>
          loginState(state.flow, error, state.selectedUserId)
        )
        .with({ status: 'unknown' }, () => state)
        .exhaustive()
    )
    .with({ status: 'clickEmailVerificationLink' }, (state) => {
      if (auth.status == 'loggedIn') {
        return postLoginState(state.flow, auth.user)
      }
      return state
    })
    .with({ status: 'lookupOrganizerNotFound' }, (state) => {
      if (auth.status == 'loggedIn') {
        return postLoginState(state.flow, auth.user)
      }
      return state
    })
    .with({ status: 'loggedIn' }, (state) => authenticatedStateChanged(state, auth))
    .with(
      {
        status: P.union(
          'lookupPending',
          'navigatingToFolder',
          'lookupChooseFolder',
          'lookupNoneFound',
          'lookupFailed'
        )
      },
      (state) => lookupAuthChanged(state, auth)
    )
    .with({ status: 'sendingVerificationEmail' }, (state) => {
      if (auth.status == 'loggedIn') {
        return postLoginState(state.flow, auth.user)
      }
      return state
    })
    .exhaustive()
}

function authenticatedStateChanged(
  state: LoggedInState,
  auth: AuthState
): UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage> {
  return match(auth)
    .returnType<UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage>>()
    .with({ status: 'loggedIn' }, ({ user }) => ({ ...state, currentUser: user }))
    .with({ status: 'loggedOut' }, ({ error }) => {
      if (state.flow.kind == 'lookup') {
        return loginState(state.flow, error)
      }
      return advanceLoading({
        status: 'loading',
        route: state.flow.route,
        auth,
        publicFolder: undefined
      })
    })
    .with({ status: 'unknown' }, () => state)
    .exhaustive()
}

function lookupAuthChanged(
  state:
    | LookupPendingState
    | NavigatingToFolderState
    | LookupChooseFolderState
    | LookupNoneFoundState
    | LookupFailedState,
  auth: AuthState
): UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage> {
  return match(auth)
    .returnType<UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage>>()
    .with({ status: 'loggedOut' }, ({ error }) => loginState(state.flow, error))
    .with({ status: 'loggedIn' }, ({ user }) => {
      if (user.id == state.currentUser.id) {
        return { ...state, currentUser: user }
      }
      return startFolderLookup(state.flow, user)
    })
    .with({ status: 'unknown' }, () => state)
    .exhaustive()
}

function loadingState(route: ClientLoginRoute, auth: AuthState): LoadingState {
  return match(route)
    .returnType<LoadingState>()
    .with({ name: P.union('client_folder', 'client_folder_logged_out') }, (route) => ({
      status: 'loading',
      route,
      auth,
      publicFolder: undefined
    }))
    .with(
      { name: P.union('client_questionnaire', 'client_questionnaire_legacy') },
      (route) => ({
        status: 'loading',
        route,
        auth,
        organizationId: undefined
      })
    )
    .exhaustive()
}

function advanceLoading(
  state: LoadingState
): UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage> {
  return match(state)
    .returnType<UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage>>()
    .with(
      {
        route: { name: P.union('client_folder', 'client_folder_logged_out') },
        auth: { status: 'loggedIn' }
      },
      (state) => ({
        status: 'loggedIn',
        flow: {
          kind: 'organizer',
          route: state.route,
          publicFolder: state.publicFolder
        },
        currentUser: state.auth.user
      })
    )
    .with(
      {
        route: { name: P.union('client_folder', 'client_folder_logged_out') },
        auth: { status: 'loggedOut' },
        publicFolder: P.nonNullable
      },
      (state) =>
        loginState(
          { kind: 'organizer', route: state.route, publicFolder: state.publicFolder },
          state.auth.error
        )
    )
    .with(
      {
        route: { name: P.union('client_questionnaire', 'client_questionnaire_legacy') },
        auth: { status: 'loggedIn' },
        organizationId: P.string
      },
      (state) =>
        startFolderLookup(
          lookupFlow(state.route, state.organizationId),
          state.auth.user
        )
    )
    .with(
      {
        route: { name: P.union('client_questionnaire', 'client_questionnaire_legacy') },
        auth: { status: 'loggedOut' },
        organizationId: P.string
      },
      (state) =>
        loginState(
          lookupFlow(state.route, state.organizationId),
          state.auth.error
        )
    )
    .otherwise((state) => state)
}

function loginState(
  flow: LoginFlow,
  authError: AuthError | undefined,
  selectedUserId?: string
): LoginState {
  if (flow.kind == 'organizer') {
    const provider = oauthProvider(flow.route)
    if (provider) {
      return {
        status: 'login',
        flow,
        loginMethod: 'oauth',
        provider,
        authError,
        selectedUserId
      }
    }
  }

  return {
    status: 'login',
    flow,
    loginMethod: 'emailVerification',
    authError,
    selectedUserId
  }
}

function postLoginState(
  flow: LoginFlow,
  currentUser: User
): UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage> {
  if (flow.kind == 'lookup') {
    return startFolderLookup(flow, currentUser)
  }
  return { status: 'loggedIn', flow, currentUser }
}

function startVerificationEmail(
  flow: LoginFlow,
  selectedUserId: string
): UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage> {
  const state: SendingVerificationEmailState = {
    status: 'sendingVerificationEmail',
    flow,
    selectedUserId
  }
  if (flow.kind == 'organizer') {
    return [
      state,
      sendOrganizerVerificationEmail(flow.route.folderId, selectedUserId, toPath(flow.route))
    ]
  }
  return [
    state,
    sendLookupVerificationEmail(
      flow.organizationId,
      flow.route.schemaId,
      selectedUserId,
      flow.clientKind,
      toPath(flow.route)
    )
  ]
}

function startFolderLookup(
  flow: LookupFlow,
  currentUser: User
): UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage> {
  const state: LookupPendingState = { status: 'lookupPending', flow, currentUser }
  const autoCreateWhenNotFound = match(flow.clientKind)
    .with('new', () => true)
    .with('returning', () => false)
    .exhaustive()

  return [
    state,
    lookupFolders(flow.organizationId, flow.route.schemaId, autoCreateWhenNotFound)
  ]
}

function lookupFlow(route: LookupRoute, organizationId: string): LookupFlow {
  return {
    kind: 'lookup',
    route,
    organizationId,
    clientKind: match(route)
      .returnType<'new' | 'returning'>()
      .with({ name: 'client_questionnaire_legacy' }, () => 'new')
      .with({ name: 'client_questionnaire' }, (route) => route.clientKind)
      .exhaustive()
  }
}

/**
 * An accepted write keeps its original owner until it settles. If route or
 * auth observation replaces that owner, the replacement is held in `resume`.
 * Commands implied by the replacement are deliberately deferred so two
 * auto-create lookups never overlap.
 */
function preserveAcceptedOperation(
  state: ActiveClientLoginFlowState,
  update: UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage>
): UpdateResult<ClientLoginFlowState, ClientLoginFlowMessage> {
  return match(state)
    .returnType<UpdateResult<ClientLoginFlowState, ClientLoginFlowMessage>>()
    .with({ status: 'sendingVerificationEmail' }, (owner) =>
      preserveOperationOwner(owner, 'verificationEmail', update)
    )
    .with({ status: 'lookupPending' }, (owner) =>
      preserveOperationOwner(owner, 'folderLookup', update)
    )
    .otherwise(() => update)
}

function preserveOperationOwner(
  owner: AcceptedOperationState,
  operation: FinishingState['operation'],
  update: UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage>
): UpdateResult<ClientLoginFlowState, ClientLoginFlowMessage> {
  const resume = deferredState(update)
  if (acceptedOperationStillOwned(owner, resume)) return update
  return { status: 'finishing', operation, resume }
}

function acceptedOperationStillOwned(
  owner: AcceptedOperationState,
  state: ActiveClientLoginFlowState
) {
  return match({ owner, state })
    .with(
      {
        owner: { status: 'sendingVerificationEmail' },
        state: { status: 'sendingVerificationEmail' }
      },
      ({ owner, state }) =>
        state.selectedUserId == owner.selectedUserId &&
        toPath(state.flow.route) == toPath(owner.flow.route)
    )
    .with(
      { owner: { status: 'lookupPending' }, state: { status: 'lookupPending' } },
      ({ owner, state }) =>
        state.currentUser.id == owner.currentUser.id &&
        toPath(state.flow.route) == toPath(owner.flow.route)
    )
    .otherwise(() => false)
}

function deferredState(
  update: UpdateResult<ActiveClientLoginFlowState, ClientLoginFlowMessage>
): ActiveClientLoginFlowState {
  if (Array.isArray(update)) return update[0]
  return update as ActiveClientLoginFlowState
}

function resumeDeferredOperation(
  state: ActiveClientLoginFlowState
): UpdateResult<ClientLoginFlowState, ClientLoginFlowMessage> {
  return match(state)
    .returnType<UpdateResult<ClientLoginFlowState, ClientLoginFlowMessage>>()
    .with({ status: 'lookupPending' }, (state) =>
      startFolderLookup(state.flow, state.currentUser)
    )
    .otherwise(() => state)
}

function foldersFound(
  state: LookupPendingState,
  folders: LookupFolderResult['folders']
): UpdateResult<ClientLoginFlowState, ClientLoginFlowMessage> {
  return match(folders)
    .returnType<UpdateResult<ClientLoginFlowState, ClientLoginFlowMessage>>()
    .with([], () => ({
      status: 'lookupNoneFound',
      flow: state.flow,
      currentUser: state.currentUser
    }))
    .with([P._], ([folder]) => navigateToFoundFolder(state, folder.id))
    .otherwise((folders) => ({
      status: 'lookupChooseFolder',
      flow: state.flow,
      currentUser: state.currentUser,
      folders
    }))
}

function navigateToFoundFolder(
  state: LookupPendingState | LookupChooseFolderState,
  folderId: string
): UpdateResult<ClientLoginFlowState, ClientLoginFlowMessage> {
  return [
    {
      status: 'navigatingToFolder',
      flow: state.flow,
      currentUser: state.currentUser,
      folderId
    },
    navigateToFolder(state.flow.route, folderId)
  ]
}

function authFromState(state: ActiveClientLoginFlowState): AuthState {
  return match(state)
    .returnType<AuthState>()
    .with({ status: P.union('idle', 'loading', 'notFound') }, (state) => state.auth)
    .with({ status: 'login' }, (state) => ({
      status: 'loggedOut',
      error: state.authError
    }))
    .with(
      {
        status: P.union(
          'sendingVerificationEmail',
          'clickEmailVerificationLink',
          'lookupOrganizerNotFound'
        )
      },
      () => ({
        status: 'loggedOut',
        error: undefined
      })
    )
    .with(
      {
        status: P.union(
          'loggedIn',
          'lookupPending',
          'navigatingToFolder',
          'lookupChooseFolder',
          'lookupNoneFound',
          'lookupFailed'
        )
      },
      (state) => ({ status: 'loggedIn', user: state.currentUser })
    )
    .exhaustive()
}

function routeFromState(state: ActiveClientLoginFlowState): ClientLoginRoute | null {
  return match(state)
    .returnType<ClientLoginRoute | null>()
    .with({ status: 'idle' }, () => null)
    .with({ status: P.union('loading', 'notFound') }, (state) => state.route)
    .with(
      {
        status: P.union(
          'login',
          'sendingVerificationEmail',
          'clickEmailVerificationLink',
          'lookupOrganizerNotFound',
          'loggedIn',
          'lookupPending',
          'navigatingToFolder',
          'lookupChooseFolder',
          'lookupNoneFound',
          'lookupFailed'
        )
      },
      (state) => state.flow.route
    )
    .exhaustive()
}

function oauthProvider(route: OrganizerRoute): OAuthProvider | undefined {
  if (route.name != 'client_folder') {
    return undefined
  }
  return match(route.aud)
    .returnType<OAuthProvider | undefined>()
    .with(P.union('karbon', 'thinkific'), (provider) => provider)
    .otherwise(() => undefined)
}

function navigateToFolder(route: LookupRoute, folderId: string) {
  return Effect.gen(function* () {
    const routing = yield* Routing
    yield* routing.navigate({
      name: 'client_folder',
      organizationSlug: route.organizationSlug,
      folderId
    })
    return null
  })
}

function logout() {
  return Effect.gen(function* () {
    const auth = yield* Auth
    yield* auth.logout()
    return null
  })
}

function unexpectedMessage(state: ClientLoginFlowState, message: ClientLoginFlowMessage): never {
  throw new Error(`Unexpected client-login message ${message.type} while ${state.status}`)
}
